import './styles.scss'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import CustomDropdown from 'Components/CustomDropdown'
import { commonMethods } from 'Utils/commonMethods'
import { getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import { CoffeeWebContext } from 'Context/CoffeeWebContext'
import ErrorMessageModal from 'Components/UIComponent/ConfirmationModal'
import { NoData } from 'Assets/Images'

const ICOCoffeeReport = ({ showError, mockICOCoffeeReport, mockMonthlyReportData, menuLock }) => {
  const { t } = useTranslation()
  const { setLoading, isLoading } = useContext(CoffeeWebContext)
  const { checkForUserAccess, dateFormatBasedOnUser } = commonMethods
  const userDetails = getLocalUserDetails()

  const [monthlyReportData, setMonthlyReportData] = useState([])
  const [fullIcoReportData, setFullIcoReportData] = useState([])
  const [yearList, setYearList] = useState([])
  const [monthList, setMonthList] = useState([])
  const [selectedYear, setSelectedYear] = useState([])
  const [selectedMonth, setSelectedMonth] = useState([])
  const [isDropdownDisabled, setIsDropdownDisabled] = useState(true)

  useEffect(() => {
    init()

    // for testing purpose
    if (mockICOCoffeeReport) {
      setMonthlyReportData(mockMonthlyReportData)
    }
  }, [])

  const init = async () => {
    setLoading(true)

    try {
      const response = await apiAdapterCoffeeWeb.getICOCoffeeReport()

      const allMonthlyReportData = response?.data?.returnLst?.map((item) => item?.months.map((month) => month?.data)).flat(2)
      const yearData = response?.data?.returnLst?.map((item) => ({ year: item.year }))

      setMonthlyReportData(allMonthlyReportData)
      setFullIcoReportData(response?.data?.returnLst)
      setYearList([{ year: 'All Data' }, ...yearData])
    } catch {
      showError({
        title: t('ERROR_TITLE'),
        message: `<p>${t('SOMETHING_WENT_WRONG')}</p><p>${t('TRY_AGAIN_MESSAGE')}</p>`,
        buttonLabel: t('OK')
      })
    } finally {
      setLoading(false)
    }
  }

  const handleYearChange = (event) => {
    const chosenYear = event.target.value

    setSelectedMonth([])
    const { year } = chosenYear

    if (year === 'All Data') {
      const allMonthlyReportData = fullIcoReportData?.map((item) => item?.months?.flatMap((month) => month.data)).flat(2)

      setMonthlyReportData(allMonthlyReportData)
      setSelectedYear(chosenYear)
      setMonthList([])
      setIsDropdownDisabled(true)
    } else {
      setIsDropdownDisabled(false)

      const monthData = fullIcoReportData?.find((item) => item.year === year)
      const monthObjects = monthData?.monthsList?.map((month) => ({ month }))
      const allMonthlyReportData = monthData?.months?.flatMap((item) => item.data)

      setMonthlyReportData(allMonthlyReportData)
      setSelectedYear(chosenYear)
      setMonthList(monthObjects)
    }
  }

  const handleMonthChange = (event) => {
    const chosenMonth = event.target.value

    const selectedMonthData = fullIcoReportData?.find((item) => item?.year === selectedYear?.year)?.months?.find((item) => item?.month === chosenMonth?.month)

    setMonthlyReportData(selectedMonthData?.data)
    setSelectedMonth(chosenMonth)
  }

  return (
    <>
      <div className="ico-report-section" data-testid="ico-main-container-test-case">
        <div className="ico-report-header" data-testid="ico-report-header-test-case">
          <div className="ico-filter-dropdowns">
            <div className="ico-report-dropdown-wrapper">
              <div className="year-dropdown">
                <CustomDropdown value={selectedYear} options={yearList} optionLabel={'year'} placeholder={t('SELECT_YEAR')} onChange={handleYearChange} className="year-selector" testId="year-dropdown-test-case" />
              </div>
              <div className="month-dropdown">
                <CustomDropdown value={selectedMonth} options={monthList} optionLabel={'month'} placeholder={t('SELECT_MONTH')} onChange={handleMonthChange} className="month-selector" disabled={isDropdownDisabled} testId="month-dropdown-test-case" />
              </div>
            </div>
          </div>

          <div className="ico-report-title-wrapper">
            <p className="ico-report-title">{t('ICO_COFFEE_REPORT')}</p>
          </div>
        </div>

        <div className="ico-report-data-container" data-testid="ico-report-summary-container-test">
          {monthlyReportData?.length === 0 && !isLoading ? (
            <div className="no-data-img-container">
              <img src={NoData} alt="NoDataFound" className="no_data_found_image" />
            </div>
          ) : (
            monthlyReportData?.map((item, index) => (
              <div key={index} className="ico-report-card" data-testid="ico-report-card-test-case">
                <div className="ico-report-card-header" data-testid="ico-report-card-header-test-case">
                  <div className="ico-report-date-info">
                    <p className="ico-report-date">{item?.isUpcoming ? 'Upcoming Report' : `Date: ${dateFormatBasedOnUser(item?.publishedDate)}`}</p>
                    <p className="ico-report-month-year">{item?.monthYear?.split(' - ').reverse().join(' - ')}</p>
                  </div>
                </div>

                <div className="ico-report-main-title" style={checkForUserAccess(menuLock)}>
                  <p>{item?.title}</p>
                </div>

                <div className="ico-report-description" style={checkForUserAccess(menuLock)}>
                  <div className="ico-report-tags-wrapper">
                    <p className={`ico-report-tags-text ${item?.isUpcoming ? 'up-coming' : ''}`}>{item?.tags}</p>
                  </div>

                  {item?.isUpcoming ? (
                    <div key={index} className="ico-upcoming-report" style={checkForUserAccess(menuLock)}>
                      {t('WILL_BE_PUBLISHED_ON')} {dateFormatBasedOnUser(item.publishedDate)}
                    </div>
                  ) : (
                    <>
                      <div className="ico-report-points">
                        <ul>
                          {item?.content
                            ?.split('\n')
                            ?.filter((point) => point?.trim() !== '')
                            ?.map((point, childIndex) => (
                              <li key={childIndex}>{point?.trim().endsWith('.') ? point?.trim() : `${point?.trim()}.`}</li>
                            ))}
                        </ul>
                      </div>
                      <div className="ico-report-link-container">
                        <span>{t('LINK_TO_REPORT')}</span>

                        {userDetails && !menuLock ? (
                          <a href={item?.reportLink} target="_blank" rel="noreferrer" className="ico-report-link" data-testid="ico-report-link">
                            {t('MONTHLY_COFFEE_REPORT')} - {item?.monthYear?.split(' - ').reverse().join(' ')}
                          </a>
                        ) : (
                          <div className="disabled-link" data-testid="disabled-link">
                            {t('MONTHLY_COFFEE_REPORT')} - {item?.monthYear?.split(' - ').reverse().join(' ')}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  )
}

export default ErrorMessageModal(ICOCoffeeReport)
